import { memo, useCallback, useState } from 'react';
import classes from 'classnames';
import copy from 'copy-to-clipboard';
import { Button } from '@/components';
import delay from '@/utils/delay';
import Placeholder from '@/components/Placeholder/Placeholder';
import CheckIcon from '../../assets/icons/check.svg?react';
import CopyIcon from '../../assets/icons/copy.svg?react';
import styles from './CopyValue.module.scss';

export type Props = {
  label: string;
  value: string | undefined;
  copyValue: string | undefined;
  className?: string;
  dataTest?: string;
  labelMsg?: string;
};

// eslint-disable-next-line react-refresh/only-export-components
export default memo(function CopyValue({
  label,
  value,
  className,
  dataTest = 'copyValue',
  labelMsg,
  copyValue,
}: Props) {
  const [copied, setCopied] = useState(false);

  const handleClick = useCallback(async () => {
    if (copyValue) {
      try {
        setCopied(true);
        copy(copyValue);
      } catch {
      } finally {
        await delay(2000);
        setCopied(false);
      }
    }
  }, [copyValue]);

  return (
    <div className={classes(styles.wrapper, className)} data-test={dataTest}>
      <div className={styles.info}>
        <div
          className={classes(
            styles.label,
            labelMsg && styles['label-with-msg'],
          )}
          data-test={`${dataTest}-label`}
        >
          {label}
          {labelMsg && (
            <span className={styles.msg} data-test={`${dataTest}-label-msg`}>
              {labelMsg}
            </span>
          )}
        </div>
        <div className={styles.value}>
          <Placeholder
            className={styles.valueLabel}
            loading={!value}
            dataTest={`${dataTest}-placeholder`}
          >
            {value}
          </Placeholder>
          <Button
            dataTest={`${dataTest}-btn`}
            disabled={!value && !copyValue}
            className={styles.button}
            buttonClassName={styles.buttonContent}
            size="s"
            onClick={handleClick}
          >
            {copied ? (
              <CheckIcon
                className={styles.icon}
                data-test={`${dataTest}-check-icon`}
              />
            ) : (
              <CopyIcon />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
});
